import type { CustomFile } from '../types';
import type { TypeExpandZipFilesConfig } from './types';

import { merge } from '../../../helper';

import extract from './extract';
import isZip from './isZip';

// ----------------------------------------------------------------------------

/*
 * Replaces every zip in a selection with the files it holds, leaving everything else
 * where it was. What comes out is an ordinary file list, so nothing downstream — the
 * validation, the grouping, the upload — has to know an archive was ever involved.
 *
 * A selection with no zip in it is handed straight back, untouched: this sits in the
 * path of every drop, and the common one must not pay for the rare one.
 *
 * The archives are opened one at a time. `extract` already reads the entries of one
 * archive concurrently, and every entry it decompresses is held in memory until the
 * upload is through — opening several at once multiplies the peak by the number of
 * archives, which is the one way a drop of a few large zips takes the tab down.
 */
async function expandFiles(files: CustomFile[], config: TypeExpandZipFilesConfig = {}): Promise<CustomFile[]> {
    const defConfig: TypeExpandZipFilesConfig = {};
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    const archives = files.filter((file): boolean => isZip(file));

    if (archives.length === 0) {
        return files;
    }

    // ------------------------------------------------------------------------

    const expanded = new Map<CustomFile, CustomFile[]>();
    let done = 0;

    defConfig.fnProgress?.(0, archives.length);

    for (const archive of archives) {
        if (defConfig.signal?.aborted === true) {
            break;
        }

        try {
            expanded.set(
                archive,
                await extract(archive, {
                    rootFolder: defConfig.rootFolder ?? true,
                    concurrency: defConfig.concurrency,
                }),
            );
        } catch (err) {
            /*
             * One archive that cannot be opened at all — truncated, not really a zip
             * despite its name — leaves the rest of the selection alone. It is warned
             * about and dropped, the same as an entry `extract` cannot read.
             */
            console.warn(`Could not open the archive "${archive.name}":`, err);
            expanded.set(archive, []);
        }

        done++;
        defConfig.fnProgress?.(done, archives.length);
    }

    // ------------------------------------------------------------------------

    /*
     * Rebuilt in the original order, with each archive standing where its own files
     * now are: a drop of one zip and three loose files keeps the three where they
     * were, which is the order the study list is built in.
     */
    return files.flatMap((file): CustomFile[] => expanded.get(file) ?? [file]);
}

// ----------------------------------------------------------------------------

export default expandFiles;
